import type { FC } from "react";
import { Button } from "@patient-watch/ui";
import { formComponents } from "@patient-watch/forms";
import type { IntervalFormValue } from "../utils/timeIntervals";
import { useCopy } from "../i18n/locale";

interface SelectOption {
    label: string;
    value: string;
    locked?: boolean;
}

export interface IntervalErrorInfo {
    startError?: string;
    endError?: string;
}

interface IntervalFieldCardsProps {
    fields: Array<{ id: string }>;
    intervals: IntervalFormValue[];
    namePrefix: string;
    computeStartOptions: (params: { index: number; intervals: IntervalFormValue[] }) => SelectOption[];
    computeEndOptions: (params: { index: number; intervals: IntervalFormValue[] }) => SelectOption[];
    getErrors: (index: number) => IntervalErrorInfo;
    canRemove: (index: number) => boolean;
    onRemove: (index: number) => void;
}

const IntervalFieldCards: FC<IntervalFieldCardsProps> = ({
    fields,
    intervals,
    namePrefix,
    computeStartOptions,
    computeEndOptions,
    getErrors,
    canRemove,
    onRemove,
}) => {
    const copy = useCopy().intervals;
    return (
        <div className="space-y-4">
            {fields.map((field, index) => {
                const { startError, endError } = getErrors(index);
                const disableRemove = !canRemove(index);

                return (
                    <div
                        key={field.id}
                        className="space-y-4 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.08)] p-4 border border-slate-200 rounded-lg"
                    >
                        <div className="flex justify-between items-start">
                            <p className="font-semibold text-slate-700 text-sm">{copy.title(index + 1)}</p>
                            <Button
                                type="button"
                                variant="destructive-ghost"
                                size="sm"
                                onClick={() => onRemove(index)}
                                className="px-2 py-1 h-8 text-sm"
                                disabled={disableRemove}
                            >
                                {copy.remove}
                            </Button>
                        </div>
                        <div className="flex sm:flex-row flex-col gap-4">
                            <div className="flex-1">
                                <formComponents.Select
                                    name={`${namePrefix}.${index}.start_time`}
                                    label={copy.startLabel}
                                    options={computeStartOptions({
                                        index,
                                        intervals,
                                    })}
                                    placeholder={copy.startPlaceholder}
                                />
                                {startError && <p className="mt-2 text-red-600 text-sm">{startError}</p>}
                            </div>
                            <div className="flex-1">
                                <formComponents.Select
                                    name={`${namePrefix}.${index}.end_time`}
                                    label={copy.endLabel}
                                    options={computeEndOptions({
                                        index,
                                        intervals,
                                    })}
                                    placeholder={copy.endPlaceholder}
                                />
                                {endError && <p className="mt-2 text-red-600 text-sm">{endError}</p>}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default IntervalFieldCards;
